import React from 'react';
import moment from 'moment-timezone';
import { connect } from 'react-redux';
import { Button, ButtonTypes } from '../components';
import Theme from '../theme';
import { Text, View, StyleSheet, Image, ImageBackground } from 'react-native';
import LoadingModal from '../ui/components/LoadingModal';
import { changeNutritionView } from '../ui/redux/uiActions';
import { Logger } from './Logger';
import UserDataContext from './UserDataContext';
import NotificationManager from '../services/notificationManager';
import { WorkoutSessionService, UserProfileService, NutritionService, InAppPurchaseService } from '../services';
import { UserServiceClient, WaitingListServiceClient } from 'fitbeat.api-clients';
import { getApiConfig, getConfig } from '../config';
import Logout from '../utils/LogoutHelper';
import Storage from '../utils/Storage';
import { AuthTokenBuilder } from '../utils/authTokenBuilder';
import Logo from '../assets/images/logo.png';
import WelcomeBackground from '../assets/images/welcome-background.png';
import { AppState, Platform } from 'react-native';
import { NutritionViews } from './DashboardViews';
import { GoogleAnalytics } from '../services/google-analytics';
import {getHomeProduct, loadUser, setInitialDataLoaded, setisinitialdataloaded} from '../auth/actions';
import {
  getWorkoutTileData,
  dashboardOpened,
} from '../SummaryDashboard/actions';
import { NavigatorService } from './NavigatorService';
import { AllModalsContainer } from '../components/AllModalsContainer';
import {isInitialDataLoadedSelector} from '../auth';

const mapStateToProps = (state) => {
  const { initialUserSetup: { dateOfBirth, legalAgreements } } = state.auth;
  const isInitialDataLoaded = isInitialDataLoadedSelector(state);
  return { dateOfBirth, legalAgreements, isInitialDataLoaded };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getWorkoutTileData: (userServiceClient) => dispatch(getWorkoutTileData(userServiceClient, true)),
    getHomeProduct: (userId, userServiceClient) => dispatch(getHomeProduct(userId, InAppPurchaseService, userServiceClient)),
    changeNutritionView: (view) => dispatch(changeNutritionView(view)),
    dashboardOpened: (workoutSessionService) => dispatch(dashboardOpened(workoutSessionService)),
    loadUser: (userServiceClient, userProfile) => dispatch(loadUser(userServiceClient, userProfile)),
    setIsInitialDataLoaded: () => dispatch(setInitialDataLoaded()),
  };
};

const defaultLoadingWrapper = (props) => (
  <>
    {props.children}
  </>
);

const userDataProvider = (Component, authProvider, LoadingWrapper = defaultLoadingWrapper) => {
  return connect(mapStateToProps, mapDispatchToProps)(class UserDataProvider extends React.Component {
    updateTimeout = null;
    isCancelling = false;
    SYNC_INTERVAL = 10000;
    userServiceClient = new UserServiceClient(getApiConfig(), fetch, console.log, new AuthTokenBuilder(), new GoogleAnalytics());
    constructor(props) {
      super(props);

      this.authProvider = authProvider;
      this.authProvider.init();
      const firebaseUserId = this.authProvider.getFirebaseUserId();
      if (!firebaseUserId && Platform.OS === 'web') {
        window.location = '/';
      }

      Logger.setUser(firebaseUserId, '', '');

      this.userProfileService = new UserProfileService(
        this.authProvider,
        this.onUserProfileChanged,
        this.setIsUserProfileServiceReady,
      );

      this.notificationManager = new NotificationManager(this.userProfileService, this.authProvider);

      this.workoutSessionService = new WorkoutSessionService(
        this.authProvider,
        this.setIsWorkoutSettingsReady,
      );

      this.waitingListServiceClient = new WaitingListServiceClient(
        getApiConfig(), fetch, new AuthTokenBuilder(), new GoogleAnalytics()
      );

      this.nutritionService = new NutritionService(this.onUserProfileChanged);

      this.state = {
        user: {
          userProfile: null,
          workoutSession: [],
          userProfileService: this.userProfileService,
          notifications: {
            manager: this.notificationManager,
            unread: null,
            syncNow: this.syncNow,
          },
        },
        authProvider: this.authProvider,
        notifications: {
          manager: this.notificationManager,
        },
        isLoading: false,
        isServerOnline: true,
      };

      this.setupDashboardView();
    }

    componentWillUnmount = () => {
      clearTimeout(this.updateTimeout);
      this.isCancelling = true;
    }

    setupDashboardView = async () => {
      const nutritionView = await Storage.get('nutritionView', NutritionViews['1DayView']);
      this.props.changeNutritionView(nutritionView);
    }

    componentDidMount = async () => {
      await this.setUpInitialUserData();
    }

    setUpInitialUserData = async () => {
      this.setIsLoading(true);

      const { dateOfBirth, legalAgreements } = this.props;
      this.isCancelling = false;
      const firebaseUser = this.authProvider.getCurrentUser();
      let initialData = {};
      try {
        const userProfile = {
          uid: firebaseUser._user.uid,
          email: firebaseUser._user.email,
        }
        this.addDeviceToken();
        if (dateOfBirth) {
          userProfile.dateOfBirth = moment(dateOfBirth, 'DD-MM-YYYY').format('YYYY-MM-DD');
        }
        userProfile.legalAgreements = legalAgreements;
        initialData = await this.userServiceClient.getAuthenticatedUser();
        Logger.debug('UserDataProvider: loaded initialData', initialData);
        if (initialData.accountType !== 'Studio') {
          this.props.getHomeProduct(initialData.uid, this.userServiceClient);
        }
        await this.setUpServices(initialData);
        this.setIsServerOnline(true);
        this.props.setIsInitialDataLoaded();
      } catch (error) {
        this.setIsServerOnline(false);
      } finally {
        this.setIsLoading(false);
      }
    }

    setUpServices = async (userProfile) => {
      await this.userProfileService.init(userProfile);
      this.setupAutoUpdater();

      await this.userProfileService.publishCurrentUserProfile();

      this.authProvider.listenFirebaseTokenChange((token) => {
        this.userProfileService.registerCurrentDevice(token);
      });
    }

    setIsServerOnline = (value) => {
      this.setState({ isServerOnline: value });
    }

    setIsLoading = (value) => {
      this.setState({ isLoading: value });
    }

    setupAutoUpdater = () => {
      const sync = async () => {
        await this.syncNow();
      }
      this.updateTimeout = setTimeout(sync, this.SYNC_INTERVAL);
    }

    syncNow = async () => {
      clearTimeout(this.updateTimeout);

      try {
        if (this.isCancelling) {
          return;
        }

        const appIsInBackground = AppState.currentState === 'background';
        if (appIsInBackground) {
          return;
        }

        await this.userProfileService.syncData();
      } catch(e) {
        Logger.leaveBreadcrumb(`Background sync failed: ${e.message}`);
      } finally {
        this.setupAutoUpdater();
      }
    }

    setIsUserProfileServiceReady = (value) => {
      this.setState({
        user: {
          ...this.state.user,
          isUserProfileServiceReady: value,
        },
      });
    }

    setIsWorkoutSettingsReady = (value) => {
      this.setState({
        user: {
          ...this.state.user,
          isWorkoutSettingsReady: value,
        },
      });
    }

    onUserProfileChanged = async (userProfile) => {
      const unreadNotifications = this.notificationManager.getUnreadNotificationCountSync();
      this.workoutSessionService.updateUserLongTermGoal(userProfile.longTermGoal);
      await this.props.loadUser(this.userServiceClient, userProfile);
      await this.setState({
        user: {
          ...this.state.user,
          userProfile,
          notifications: {
            ...this.state.user.notifications,
            unread: unreadNotifications,
          },
        },
      });
    }

    addDeviceToken = async () => {
      if (Platform.OS == 'web') {
        return;
      }

      const deviceDetails = await this.authProvider.getCurrentDevice();
      this.userServiceClient.addDeviceToken(deviceDetails.token, deviceDetails.platform);
    }

    render() {
      const {
        user: {
          userProfile,
          waitingList,
          isWorkoutSettingsReady,
          isUserProfileServiceReady,
          notifications,
        },
        isLoading,
        isServerOnline,
      } = this.state;

      if (this.props.isInitialDataLoaded && userProfile) {
        return (
          <UserDataContext.Provider
            key="unique"
            value={{
              userProfile,
              waitingList,
              notifications,
              isWorkoutSettingsReady,
              isUserProfileServiceReady,
              userProfileService: this.userProfileService,
              workoutSessionService: this.workoutSessionService,
              nutritionService: this.nutritionService,
              userServiceClient: this.userServiceClient,
              waitingListServiceClient: this.waitingListServiceClient,
              setUpInitialUserData: this.setUpInitialUserData,
            }}>
            <Component ref={(navigator) => navigator && NavigatorService.setTopLevelNavigatorContainerTwo(
              navigator.navigation || navigator,
            )} />
            <AllModalsContainer
              userProfile={userProfile}
              userProfileService={this.userProfileService}
              waitingListServiceClient={this.waitingListServiceClient}
              workoutSessionService={this.workoutSessionService}
            />
          </UserDataContext.Provider>
        );
      }

      let message = null;
      if (!isLoading && !isServerOnline) {
        message = 'Failed to connect to Fitillion, possibly due to a connection issue. Please try again.';
      }

      return (
        <LoadingWrapper {...this.props}>
          <View style={[styles.container, !isLoading && message && { backgroundColor: Theme.GREY_DARK }]}>
            <LoadingModal isLoading={isLoading} />
            {!isLoading && message &&
            <View style={styles.container}>
            <ImageBackground source={WelcomeBackground} style={styles.imageContainer}>
              <View style={styles.logoContainer}>
                <Image source={Logo} style={styles.logo} />
                <Text style={styles.offlineText}>{message}</Text>
              </View>
            </ImageBackground>
            <View style={styles.footer}>
              <Button
                title="Retry"
                containerStyle={styles.buttonStyle}
                fontColor={Theme.WHITE}
                onPress={this.setUpInitialUserData}
              />
              <Button
                title="Logout"
                containerStyle={styles.buttonStyle}
                fontColor={Theme.GREY}
                onPress={() => Logout('AuthNavigator')}
                buttonType={ButtonTypes.OUTLINE}
              />
            </View>
          </View>
            }
          </View>
        </LoadingWrapper>
      );
    }
  });
}

const styles = StyleSheet.create({
  container: {
    flex: 1,
    minHeight: '100%',
    justifyContent: 'center',
    backgroundColor: Theme.WHITE,
  },
  imageContainer: {
    flex: 1,
    width: null,
    height: null,
  },
  logoContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  logo: {
    width: 250,
    height: 100,
    resizeMode: 'contain',
  },
  footer: {
    paddingVertical: 25,
    paddingHorizontal: 15,
    backgroundColor: Theme.GREY_DARK,
  },
  buttonStyle: {
    marginVertical: 5,
  },
  offlineText: {
    fontSize: 20,
    color: Theme.WHITE,
    paddingVertical: 15,
    textAlign: 'center',
    paddingHorizontal: 15
  },
});

export default userDataProvider;
